// 鶏 (inverted-jester) の「焦り狂い」案A (2026-11-03 作・11-04 から配布版の既定・前の仕組みは開発版 `?roosterFrenzy=off`)。
//   段の表と切り替え = src/rooster_frenzy_spec.ts。設計 = docs/research/2026-11-03-rooster-frenzy-design.md。
//
//   段 0 平静 → 1 苛立ち → 2 焦り → 3 狂い。
//     上がる = 鬨ずらしの連の爪を全部弾いた (ガード・回避は数えない)。連の終わりに +1。
//     下がる = 連の途中で主人公が被弾した。連の終わりに −1 (既定 hit1)・段0へ (`&roosterFrenzyFall=reset`)。
//     ガード・回避で凌いだだけの連は段を保つ。主人公がボスを殴っても段は動かない。段が変わるのは連の切れ目だけ。
//   段3 の狂い鬨 (鳴き→爪 を3つ休みなし) を弾き切ると、息切れ (約2秒・殴れる) の後で段0へ戻る。
//   段2 から上は縦の技 (暁呼び・宙廻り・鳴き挟み) を出さず、鳴き直しの連に替える。
//   加速 (src/rooster_rush.ts・震え 3.2px) は焦り狂いの間は止める (動くのは開発版 `?roosterFrenzy=off` だけ)。
import { ATTACKS } from './attacks'
import { FIXED_DT } from './balance'
import { BOSS_ATTACK_TIMELINES } from './boss_pose_timeline'
import { roosterFrenzyTimeline } from './rooster_crow_art'
import {
  FRENZY_STAGES, FRENZY_VARIANTS, ROOSTER_FRENZY, ROOSTER_FRENZY_BOSS_ID, frenzyVariant, roosterFrenzyFall, roosterFrenzyOn,
  type FrenzyStage,
} from './rooster_frenzy_spec'
import type { Boss, BossString, SfxId, StringBeat } from './types'

export interface RoosterFrenzyState {
  stage: FrenzyStage
  /** いまの連の途中か。 */
  inString: boolean
  /** この連で弾けば段が上がる爪の数 (鬨ずらしの連だけ。0 = 上がらない連)。 */
  claws: number
  parries: number
  struck: boolean
  /** 段3 を弾き切って息切れの戻りに入った。 */
  breath: boolean
}

const TOKI_STRINGS = new Set(['rooster_tokiShort', 'rooster_tokiLong'])
const BASE_TOKI = ['tokiZurashiShort', 'tokiZurashiLong'] as const

function stateOf(boss: Boss): RoosterFrenzyState {
  return boss.roosterFrenzy ?? { stage: 0, inString: false, claws: 0, parries: 0, struck: false, breath: false }
}
export function roosterFrenzyStage(boss: Boss): FrenzyStage { return boss.roosterFrenzy?.stage ?? 0 }

let installed = false
/** 段0・段1 の技と絵を登録する (焦り狂いの時だけ・初めて要る時に1回)。 */
export function ensureRoosterFrenzyAttacks(): void {
  if (installed) return
  installed = true
  const tl = BOSS_ATTACK_TIMELINES[ROOSTER_FRENZY_BOSS_ID] ?? {}
  for (const v of FRENZY_VARIANTS) {
    const base = ATTACKS[v.base]!
    ATTACKS[v.id] = {
      ...base, id: v.id, label: `${base.label}・焦り狂い段${v.id.includes('0') ? 0 : 1}`,
      chargeMs: v.chargeMs, recoveryMs: v.recoveryMs, comboHits: v.hits, comboGapMs: v.gapsMs[0], comboGapsMs: [...v.gapsMs],
    }
    tl[v.id] = roosterFrenzyTimeline(v)
  }
  BOSS_ATTACK_TIMELINES[ROOSTER_FRENZY_BOSS_ID] = tl
}

function hitsOf(attackId: string): number { return ATTACKS[attackId]?.comboHits ?? 1 }
function pickToki(): string { return BASE_TOKI[Math.random() < 0.5 ? 0 : 1] }

/** 段に合わせて、選ばれた連を組み替える。null = そのまま。 */
export function frenzyStringFor(stage: FrenzyStage, str: BossString, rnd: () => string = pickToki): BossString | null {
  const s = FRENZY_STAGES[stage]
  const toki = TOKI_STRINGS.has(str.id)
  if (s.chain === 0) {
    if (!toki) return null
    const v = str.id === 'rooster_tokiShort' ? s.short! : s.long!
    return { ...str, id: `${str.id}_frenzy${stage}`, beats: [{ attackId: v.id, recoveryOverrideMs: v.recoveryMs }] }
  }
  // 段2・段3: 縦の技も含め、鳴き直しの連 (短い・長いの組は毎回変わる)。1つ目は選ばれた鬨ずらしの形を残す。
  const first = str.id === 'rooster_tokiLong' ? 'tokiZurashiLong' : str.id === 'rooster_tokiShort' ? 'tokiZurashiShort' : rnd()
  const beats: StringBeat[] = [{ attackId: first }]
  for (let i = 1; i < s.chain; i++) beats.push({ attackId: rnd(), gapMs: ROOSTER_FRENZY.chainGapMs })
  const last = beats.length - 1
  beats[last] = { ...beats[last]!, recoveryOverrideMs: stage === 3 ? ROOSTER_FRENZY.stage3RecoveryMs : ROOSTER_FRENZY.stage2RecoveryMs }
  return { id: `rooster_frenzy${stage}_chain`, beats, weight: 1, allowRepeat: true }
}

/** 弾けば段が上がる爪の数 (鬨ずらしの連だけ)。 */
function clawsOf(str: BossString): number {
  return str.beats.every(b => b.attackId.startsWith('toki')) ? str.beats.reduce((n, b) => n + hitsOf(b.attackId), 0) : 0
}

/** 待機の速さ (段が上がるほど次の連が早い)。 */
export function roosterFrenzyPreTick(boss: Boss): Boss {
  if (!roosterFrenzyOn(boss) || boss.state !== 'idle') return boss
  const rate = FRENZY_STAGES[stateOf(boss).stage].idleRate
  return rate > 1 ? { ...boss, stateElapsed: boss.stateElapsed + FIXED_DT * (rate - 1) } : boss
}

/**
 * updateBoss の直後に1回。連の頭 (待機などから溜めへ入った tick) で、段に合わせて連を組み替える。
 *   予告の音・床技の湧きより前にかける (組み替えた技の音が鳴るように)。
 */
export function roosterFrenzyShape<R extends { boss: Boss }>(prev: Boss, result: R, rnd?: () => string): R {
  const b = result.boss
  if (!roosterFrenzyOn(b)) return result
  const startsString = b.state === 'telegraph' && b.currentString && b.beatIndex === 0
    && prev.state !== 'telegraph' && prev.state !== 'beatGap'
  if (!startsString) return result
  ensureRoosterFrenzyAttacks()
  const st = stateOf(b)
  const str = frenzyStringFor(st.stage, b.currentString!, rnd) ?? b.currentString!
  const atk = ATTACKS[str.beats[0]!.attackId]!
  const next: Boss = {
    ...b, currentString: str, currentAttack: atk, lastAttackId: atk.id,
    roosterFrenzy: { ...st, inString: true, claws: clawsOf(str), parries: 0, struck: false, breath: false },
  }
  return { ...result, boss: next }
}

/**
 * 当たりの解決の後に1回。弾き・被弾を数え、段3 を弾き切ったら息切れを入れ、連の終わりで段を動かす。
 *   prev = この tick の更新前のボス、next = 当たりの解決後のボス、sfx = この tick に鳴る音。
 */
export function roosterFrenzyAfterHits(prev: Boss, next: Boss, sfx: readonly SfxId[]): { boss: Boss; sfx: SfxId[] } {
  if (!roosterFrenzyOn(next)) return { boss: next, sfx: [] }
  let st = stateOf(next)
  const out: SfxId[] = []
  if (st.inString) {
    if (sfx.includes('parry')) st = { ...st, parries: st.parries + 1 }
    if (sfx.includes('hit_player')) st = { ...st, struck: true }
  }
  let boss: Boss = next
  // 段3 の最後の爪が終わった所で弾き切っていれば、戻りを息切れに延ばす。
  if (st.inString && st.stage === 3 && !st.breath && !st.struck && st.claws > 0 && st.parries >= st.claws
    && prev.state === 'active' && next.state === 'recovery' && next.currentString) {
    const beats = next.currentString.beats.map((bt, i) => i === next.beatIndex ? { ...bt, recoveryOverrideMs: ROOSTER_FRENZY.breathMs } : bt)
    boss = { ...boss, currentString: { ...next.currentString, beats } }
    st = { ...st, breath: true }
  }
  // 連の終わり = 戻りから待機へ・崩れた。
  const ended = st.inString && ((next.state === 'idle' && prev.state !== 'idle') || (next.state === 'staggered' && prev.state !== 'staggered'))
  if (ended) {
    const from = st.stage
    let stage: FrenzyStage = from
    if (st.struck) stage = roosterFrenzyFall() === 'reset' ? 0 : (Math.max(0, from - 1) as FrenzyStage)
    else if (st.breath) stage = 0
    else if (next.state !== 'staggered' && st.claws > 0 && st.parries >= st.claws && from < 3) stage = (from + 1) as FrenzyStage
    if (stage > from) out.push('rooster_rush_tick')
    st = { stage, inString: false, claws: 0, parries: 0, struck: false, breath: false }
  }
  return { boss: { ...boss, roosterFrenzy: st }, sfx: out }
}

/** 息切れの最中か (描画用)。 */
export function roosterFrenzyBreathing(boss: Boss): boolean {
  return roosterFrenzyOn(boss) && !!boss.roosterFrenzy?.breath && boss.state === 'recovery'
}

/**
 * 段0・段1 の鳴きの音。短い鳴きは「コッ」を声ごとに1つずつ (拍が段で変わるので録った3声の音は使えない)、
 *   長い鳴きは配布版と同じ音 (鳴き止みの時刻は同じ)。溜めの prevMs → nowMs の間に鳴らす音 (溜めに入った tick は prevMs = -1)。
 */
export function roosterFrenzyTellSfx(attackId: string, prevMs: number, nowMs: number, v1Long: SfxId): SfxId[] {
  const v = frenzyVariant(attackId)
  if (!v) return []
  if (v.clucksAtMs.length === 0) return prevMs < 0 ? [v1Long] : []
  return v.clucksAtMs.some(ms => prevMs < ms && nowMs >= ms) ? ['rooster_frenzy_cluck'] : []
}
export function isRoosterFrenzyAttack(attackId: string): boolean { return !!frenzyVariant(attackId) }
